import { type AnchorHTMLAttributes, type MouseEvent, useState } from 'react';
import {
  downloadAuthenticatedUrl,
  isGeneratedDocumentDownloadUrl,
  resolveApiHref,
} from '../lib/api';

type Props = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'> & {
  href: string;
  /** Force authenticated blob download even if href is not a generated-doc URL. */
  forceAuthenticated?: boolean;
  filenameHint?: string;
};

/**
 * Same-origin download link that uses credentials (cookie / dev API key) via
 * fetch+blob for protected API paths. External http(s) links open normally.
 */
export default function AuthenticatedDownloadLink({
  href,
  forceAuthenticated = false,
  filenameHint,
  children,
  className,
  ...rest
}: Props) {
  const [busy, setBusy] = useState(false);
  const resolved = resolveApiHref(href);
  const useAuth =
    forceAuthenticated ||
    isGeneratedDocumentDownloadUrl(href) ||
    resolved.startsWith('/api/') ||
    resolved.includes('/api/v1/');

  const onClick = async (e: MouseEvent<HTMLAnchorElement>) => {
    if (!useAuth || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await downloadAuthenticatedUrl(href, filenameHint);
    } finally {
      setBusy(false);
    }
  };

  return (
    <a
      {...rest}
      href={resolved}
      onClick={(ev) => {
        void onClick(ev);
      }}
      aria-busy={busy || undefined}
      className={className}
    >
      {children}
    </a>
  );
}
